import { useEffect, useState } from 'react';
import { Autocomplete, Box, IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { EKeyAction, EPageName } from '../data/Constants';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import LanguageSetting from './LanguageSetting';
import { useDispatch, useSelector } from 'react-redux';
import { getProductModels } from '../store/states/AppSlice';
import { AppDispatch, AppState } from '../store/AppStore';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { setEditMode } from '../store/states/ProcosSlice';

import type { IIProductSearchBarProps } from '../../types';

//Returns the Search Bar component to search the product models in home page
const ProductSearchBar = ({
  location,
  t,
  searchProduct,
  resetSearch,
  searchValue,
}: IIProductSearchBarProps) => {
  return (
    <>
      {location.pathname === EPageName.Home && (
        <Tooltip title={t('labels.searchproduct')}>
          <Autocomplete
            onInputChange={(_e, value, reason) => resetSearch(value, reason)}
            id="product-search-box"
            freeSolo
            options={[]}
            className="search-bar"
            data-testid="search-bar"
            value={searchValue}
            renderInput={(params) => (
              <TextField
                {...params}
                onKeyDown={searchProduct}
                className="search-inner-text"
                placeholder={t('labels.search')}
                slotProps={{
                  ...params.slotProps,
                  input: {
                    ...params.slotProps.input,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
          />
        </Tooltip>
      )}
    </>
  );
};

//Style Class names for disabled and enabled icons
const getClassName = (value: boolean) => (value ? 'action-icon-disabled' : 'action-icon');

/**
 * Main component to Search Product Models  and change Languge in all the pages and Action panel Icons.
 * @returns {JSX.Element}  which return the product search bar and language component
 */
const ActionPanel = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');

  const procosData = useSelector((state: AppState) => ({
    isEditMode: state.procos.isEditMode,
    isDataUpdated: state.procos.isDataUpdated,
  }));
  const productModels = useSelector((state: AppState) => state.app.productModels);

  const isProcosPage = location.pathname.includes(EPageName.Procos);
  const dispatch = useDispatch<AppDispatch>();

  //remove search value when refresh the product List
  useEffect(() => {
    if (productModels?.totalCount > 1 && productModels.searchKey === '') {
      setSearchValue('');
    }
  }, [productModels]);

  //remove search value when navigate from home Page to other pages
  useEffect(() => {
    if (location.pathname !== EPageName.Home) {
      setSearchValue('');
    }
  }, [location.pathname]);

  //To handle Edit Action
  const handleEditMode = (value: boolean) => {
    dispatch(setEditMode({ isEdit: value }));
  };

  //get the search product from the API
  const searchProduct = (e) => {
    if (e.code === EKeyAction.Enter && e.target.value.length > 2) {
      setSearchValue(e.target.value);
      dispatch(getProductModels({ productSearch: e.target.value.trim(), page: 1, limit: 15 }));
    }
  };

  //clear the search value and reset product list
  const resetSearch = (value: string, reason: string) => {
    if ((value === '' && searchValue !== '') || (value !== '' && reason === EKeyAction.Clear)) {
      setSearchValue('');
      dispatch(getProductModels({ page: 1, limit: 15 }));
    }
  };

  return (
    <Box className="rowFlex w-100 justify-end">
      <ProductSearchBar
        t={t}
        location={location}
        resetSearch={resetSearch}
        searchProduct={searchProduct}
        searchValue={searchValue}
      />
      {isProcosPage && (
        <>
          <Tooltip title={t('tooltip.edit')}>
            <span>
              <IconButton
                className={getClassName(procosData.isEditMode)}
                onClick={() => handleEditMode(true)}
                disabled={procosData.isEditMode}
              >
                <EditIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('tooltip.view')}>
            <span>
              <IconButton
                className={getClassName(!procosData.isEditMode || procosData.isDataUpdated)}
                onClick={() => handleEditMode(false)}
                disabled={!procosData.isEditMode || procosData.isDataUpdated}
              >
                <VisibilityIcon />
              </IconButton>
            </span>
          </Tooltip>
        </>
      )}
      &emsp;
      <LanguageSetting />
    </Box>
  );
};

export default ActionPanel;
